import { FC, useMemo } from "react";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { LocationConfidenceScoresLabelEnum, ProviderActionStatusEnum } from "@app/projects/enumerators";
import { AccessPointFilterState, ServicingAccessPoint } from "@app/projects/network-assessment/provider-hub/interfaces";
import { convertTextToKebabCase } from "@app/shared/utility/convertTextToKebabCase";

interface AccessPointFilterPanelProps {
  /** Full unfiltered access point list — used to determine which sub-filter options to show. */
  allAccessPoints: readonly ServicingAccessPoint[];
  filterState: AccessPointFilterState;
  onChange: (next: AccessPointFilterState) => void;
  showLocationConfidence: boolean;
  showTerminated: boolean;
}

// Access points only carry Fully_ statuses; Partially_ is a provider-level aggregate concept.
const TARGETED_STATUSES = [ProviderActionStatusEnum.FullyTargeted];
const EXCLUDED_STATUSES = [ProviderActionStatusEnum.FullyExcluded];
const TERMINATED_STATUSES = [ProviderActionStatusEnum.FullyTerminated];

// Location confidence options in AC-specified display order (No Score → High).
// Each object's keys are already alphabetical (label < value), satisfying sort-keys.
const LOCATION_CONFIDENCE_OPTIONS: ReadonlyArray<{ label: string; value: LocationConfidenceScoresLabelEnum | null }> = [
  { label: "No Score Available", value: null },
  { label: "Very Low", value: LocationConfidenceScoresLabelEnum.VeryLow },
  { label: "Low", value: LocationConfidenceScoresLabelEnum.Low },
  { label: "Medium", value: LocationConfidenceScoresLabelEnum.Medium },
  { label: "High", value: LocationConfidenceScoresLabelEnum.High },
];

export const AccessPointFilterPanel: FC<AccessPointFilterPanelProps> = ({
  allAccessPoints,
  filterState,
  onChange,
  showLocationConfidence,
  showTerminated,
}) => {
  const apPresence = useMemo(() => {
    let hasExcluded = false;
    let hasMarket = false;
    let hasNetNew = false;
    let hasNetwork = false;
    let hasProspect = false;
    let hasTargeted = false;
    let hasTerminated = false;
    const presentConfidences = new Set<LocationConfidenceScoresLabelEnum | null>();
    for (const ap of allAccessPoints) {
      if (ap.providerNetworkType === ProviderNetworkTypeEnum.Market)   hasMarket   = true;
      if (ap.providerNetworkType === ProviderNetworkTypeEnum.NetNew)   hasNetNew   = true;
      if (ap.providerNetworkType === ProviderNetworkTypeEnum.Network)  hasNetwork  = true;
      if (ap.providerNetworkType === ProviderNetworkTypeEnum.Prospect) hasProspect = true;
      if (EXCLUDED_STATUSES.includes(ap.providerActionStatus))         hasExcluded  = true;
      if (TARGETED_STATUSES.includes(ap.providerActionStatus))         hasTargeted  = true;
      if (TERMINATED_STATUSES.includes(ap.providerActionStatus))       hasTerminated = true;
      presentConfidences.add(ap.locationConfidenceScore ?? null);
    }
    return { hasExcluded, hasMarket, hasNetNew, hasNetwork, hasProspect, hasTargeted, hasTerminated, presentConfidences };
  }, [allAccessPoints]);

  const { hasExcluded, hasMarket, hasNetNew, hasNetwork, hasProspect, hasTargeted, hasTerminated, presentConfidences } = apPresence;

  const showActionStatusGroup = hasTargeted || hasExcluded || (showTerminated && hasTerminated);

  // Each sub-filter is only shown when at least one AP has that network type.
  const visibleOutOfNetworkSubs = useMemo<ProviderNetworkTypeEnum[]>(() => {
    const subs: ProviderNetworkTypeEnum[] = [];
    if (hasProspect) subs.push(ProviderNetworkTypeEnum.Prospect);
    if (hasMarket) subs.push(ProviderNetworkTypeEnum.Market);
    if (hasNetNew) subs.push(ProviderNetworkTypeEnum.NetNew);
    return subs;
  }, [hasMarket, hasNetNew, hasProspect]);

  // "Out of Network" parent is checked only when every visible sub-filter is selected.
  const outOfNetworkChecked = visibleOutOfNetworkSubs.length > 0 && visibleOutOfNetworkSubs.every(t => filterState.providerSources.includes(t));

  const toggleSource = (type: ProviderNetworkTypeEnum) => {
    const next = filterState.providerSources.includes(type)
      ? filterState.providerSources.filter(s => s !== type)
      : [...filterState.providerSources, type];
    onChange({ ...filterState, providerSources: next });
  };

  const toggleOutOfNetwork = () => {
    if (outOfNetworkChecked) {
      // Deselect all sub-filters; preserve In-Network if selected.
      onChange({
        ...filterState,
        providerSources: filterState.providerSources.filter(s => !visibleOutOfNetworkSubs.includes(s)),
      });
    } else {
      // Select all visible sub-filters; preserve In-Network if already selected.
      const without = filterState.providerSources.filter(s => !visibleOutOfNetworkSubs.includes(s));
      onChange({ ...filterState, providerSources: [...without, ...visibleOutOfNetworkSubs] });
    }
  };

  const isActionCategoryActive = (statuses: ProviderActionStatusEnum[]) =>
    statuses.some(s => filterState.actionStatuses.includes(s));

  const toggleActionCategory = (statuses: ProviderActionStatusEnum[]) => {
    onChange({
      ...filterState,
      actionStatuses: isActionCategoryActive(statuses)
        ? filterState.actionStatuses.filter(s => !statuses.includes(s))
        : [...filterState.actionStatuses, ...statuses],
    });
  };

  const toggleConfidence = (value: LocationConfidenceScoresLabelEnum | null) => {
    const next = filterState.locationConfidences.includes(value)
      ? filterState.locationConfidences.filter(c => c !== value)
      : [...filterState.locationConfidences, value];
    onChange({ ...filterState, locationConfidences: next });
  };

  return (
    <div className="provider-profile__ap-filter-panel">

      {/* By Provider Source */}
      <div className="provider-profile__ap-filter-group">
        <p className="provider-profile__ap-filter-group-label">By Provider Source</p>
        {hasNetwork && (
          <QESCheckbox
            checked={filterState.providerSources.includes(ProviderNetworkTypeEnum.Network)}
            label="In-Network"
            onChange={() => toggleSource(ProviderNetworkTypeEnum.Network)}
            testId="ap-filter-source-network"
          />
        )}
        {visibleOutOfNetworkSubs.length > 0 && (
          <>
            <QESCheckbox
              checked={outOfNetworkChecked}
              label="Out of Network"
              onChange={toggleOutOfNetwork}
              testId="ap-filter-source-out-of-network"
            />
            <div className="provider-profile__ap-filter-sub-group">
              {hasProspect && (
                <QESCheckbox
                  checked={filterState.providerSources.includes(ProviderNetworkTypeEnum.Prospect)}
                  label="Prospect"
                  onChange={() => toggleSource(ProviderNetworkTypeEnum.Prospect)}
                  testId="ap-filter-source-prospect"
                />
              )}
              {hasMarket && (
                <QESCheckbox
                  checked={filterState.providerSources.includes(ProviderNetworkTypeEnum.Market)}
                  label="Market"
                  onChange={() => toggleSource(ProviderNetworkTypeEnum.Market)}
                  testId="ap-filter-source-market"
                />
              )}
              {hasNetNew && (
                <QESCheckbox
                  checked={filterState.providerSources.includes(ProviderNetworkTypeEnum.NetNew)}
                  label="Net New"
                  onChange={() => toggleSource(ProviderNetworkTypeEnum.NetNew)}
                  testId="ap-filter-source-net-new"
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* By Action Status */}
      {showActionStatusGroup && (
        <div className="provider-profile__ap-filter-group">
          <p className="provider-profile__ap-filter-group-label">By Action Status</p>
          {hasTargeted && (
            <QESCheckbox
              checked={isActionCategoryActive(TARGETED_STATUSES)}
              label="Targeted"
              onChange={() => toggleActionCategory(TARGETED_STATUSES)}
              testId="ap-filter-action-targeted"
            />
          )}
          {hasExcluded && (
            <QESCheckbox
              checked={isActionCategoryActive(EXCLUDED_STATUSES)}
              label="Excluded"
              onChange={() => toggleActionCategory(EXCLUDED_STATUSES)}
              testId="ap-filter-action-excluded"
            />
          )}
          {showTerminated && hasTerminated && (
            <QESCheckbox
              checked={isActionCategoryActive(TERMINATED_STATUSES)}
              label="Terminated"
              onChange={() => toggleActionCategory(TERMINATED_STATUSES)}
              testId="ap-filter-action-terminated"
            />
          )}
        </div>
      )}

      {/* By Location Confidence (shown only when project option + feature flag are active) */}
      {showLocationConfidence && (
        <div className="provider-profile__ap-filter-group">
          <p className="provider-profile__ap-filter-group-label">By Location Confidence</p>
          {LOCATION_CONFIDENCE_OPTIONS.filter(({ value }) => presentConfidences.has(value)).map(({ label, value }) => (
            <QESCheckbox
              checked={filterState.locationConfidences.includes(value)}
              key={label}
              label={label}
              onChange={() => toggleConfidence(value)}
              testId={`ap-filter-confidence-${convertTextToKebabCase(label)}`}
            />
          ))}
        </div>
      )}

    </div>
  );
};
